<template>
  <el-dropdown trigger="click" @command="handleCommand">
    <div class="language-switcher">
      <span class="language-text">{{ currentLanguageText }}</span>
      <el-icon class="language-icon"><ArrowDown /></el-icon>
    </div>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="lang in languages"
          :key="lang.value"
          :command="lang.value"
          :class="{ 'is-active': currentLocale === lang.value }"
        >
          {{ lang.label }}
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowDown } from '@element-plus/icons-vue';
import { setLocale } from '@/i18n';
import zhCn from 'element-plus/dist/locale/zh-cn.mjs';
import en from 'element-plus/dist/locale/en.mjs';
import zhTw from 'element-plus/dist/locale/zh-tw.mjs';

const { locale, t } = useI18n();

const languages = [
  { label: '简体中文', value: 'zh-cn' },
  { label: 'English', value: 'en' },
  { label: '繁體中文', value: 'zh-tw' }
];

const currentLocale = computed(() => locale.value);

const currentLanguageText = computed(() => {
  const lang = languages.find(l => l.value === currentLocale.value);
  return lang ? lang.label : '简体中文';
});

// Element Plus 语言包映射
const elementLocaleMap: Record<string, any> = {
  'zh-cn': zhCn,
  'en': en,
  'zh-tw': zhTw
};

const handleCommand = (command: 'zh-cn' | 'en' | 'zh-tw') => {
  setLocale(command);
  // 更新 Element Plus 的语言
  const elementLocale = elementLocaleMap[command];
  if (elementLocale) {
    // 通过事件通知 App.vue 更新 Element Plus 的 locale
    window.dispatchEvent(new CustomEvent('locale-change', { detail: { locale: elementLocale } }));
  }
};

// 暴露当前语言给父组件
defineExpose({
  currentLocale
});
</script>

<style scoped lang="scss">
.language-switcher {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 7px 12px;
  border: 1px solid var(--border-color-middle);
  border-radius: 4px;
  color: var(--color-text-regular);
  transition: all 0.3s;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .language-text {
    font-size: 14px;
    margin-right: 4px;
  }

  .language-icon {
    font-size: 12px;
  }
}

:deep(.el-dropdown-menu__item.is-active) {
  color: var(--color-primary);
  font-weight: 600;
}
</style>

